import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { retryWhen, mergeMap } from 'rxjs/operators';
import {
  HttpRetryPolicy,
  calculateRetryDelay,
  isRetryableError,
} from '../../utils/http-request/http-retry-policy';

/**
 * Service that wraps HTTP calls with configurable retry policies
 */
@Injectable({
  providedIn: 'root',
})
export class PolicyWrapperService {
  /**
   * Wraps an HTTP call with retry policy and exponential backoff
   * @param httpCall - Function that returns the HTTP Observable to execute
   * @param config - Configuration object containing retry policy
   * @returns Observable with retry logic applied
   *
   * @example
   * this.policy.For(
   *   () => this.http.post(url, data),
   *   { retryPolicy: DEFAULT_RETRY_POLICY }
   * )
   */
  For<T>(httpCall: () => Observable<T>, config: { retryPolicy: HttpRetryPolicy }): Observable<T> {
    return httpCall().pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, attempt) => {
            // Check if max retries exceeded
            if (attempt >= config.retryPolicy.maxRetries) {
              return throwError(() => error);
            }

            // Check if error status is retryable
            const status = error?.status ?? 0;
            if (!isRetryableError(status, config.retryPolicy)) {
              return throwError(() => error);
            }

            // Calculate delay with backoff strategy
            const delay = calculateRetryDelay(attempt, config.retryPolicy);
            console.log(
              `[PolicyWrapper] Retry attempt ${attempt + 1}/${config.retryPolicy.maxRetries} after ${delay}ms (status: ${status})`
            );

            return timer(delay);
          })
        )
      )
    );
  }
}
